import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { defaultPreferences } from '../catalog';
import type { ReportJob, ReportPreferences } from '../types';
import {
  cancelReport,
  createReport,
  interpretReport,
  loadReports,
  pollReport,
  savePreferences,
} from './actions';

export interface ReportsState {
  scope: string | null;
  jobs: ReportJob[];
  preferences: ReportPreferences;
  selectedId: string | null;
  loading: boolean;
  creating: boolean;
  planning: boolean;
  error: string | null;
  requestId: string | null;
  requests: string[];
}
const initialState: ReportsState = {
  scope: null,
  jobs: [],
  preferences: defaultPreferences,
  selectedId: null,
  loading: false,
  creating: false,
  planning: false,
  error: null,
  requestId: null,
  requests: [],
};
const slice = createSlice({
  name: 'negociacaoRelatorios',
  initialState,
  reducers: {
    resetReports: () => initialState,
    selectReport: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder.addCase(interpretReport.pending, (state, action) => {
      if (state.scope === action.meta.arg.scope) {
        state.planning = true;
        state.error = null;
        state.requests.push(action.meta.requestId);
      }
    });
    builder.addCase(interpretReport.fulfilled, (state, action) => {
      if (state.scope !== action.meta.arg.scope || !state.requests.includes(action.meta.requestId))
        return;
      state.planning = false;
      state.requests = state.requests.filter((id) => id !== action.meta.requestId);
    });
    builder.addCase(interpretReport.rejected, (state, action) => {
      if (state.scope !== action.meta.arg.scope || !state.requests.includes(action.meta.requestId))
        return;
      state.planning = false;
      state.requests = state.requests.filter((id) => id !== action.meta.requestId);
      state.error =
        'Não consegui entender seu pedido agora. Seu texto continua aqui para tentar novamente.';
    });
    builder.addCase(loadReports.pending, (state, action) => {
      if (state.scope !== action.meta.arg) Object.assign(state, initialState);
      state.scope = action.meta.arg;
      state.loading = true;
      state.error = null;
      state.requestId = action.meta.requestId;
    });
    builder.addCase(loadReports.fulfilled, (state, action) => {
      if (state.scope !== action.meta.arg || state.requestId !== action.meta.requestId) return;
      state.jobs = action.payload.jobs.filter((job) => job.workspaceId === state.scope);
      state.preferences = action.payload.preferences;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loadReports.rejected, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.loading = false;
      state.error = 'Não foi possível carregar seus relatórios. Tente novamente.';
    });
    builder.addCase(createReport.pending, (state, action) => {
      if (state.scope === action.meta.arg.scope) {
        state.creating = true;
        state.error = null;
        state.requests.push(action.meta.requestId);
      }
    });
    for (const thunk of [createReport, pollReport, cancelReport]) {
      if (thunk !== createReport)
        builder.addCase(thunk.pending, (state, action) => {
          if (state.scope === action.meta.arg.scope) state.requests.push(action.meta.requestId);
        });
      builder.addCase(thunk.fulfilled, (state, action) => {
        if (
          state.scope !== action.meta.arg.scope ||
          action.payload.workspaceId !== state.scope ||
          !state.requests.includes(action.meta.requestId)
        )
          return;
        state.requests = state.requests.filter((id) => id !== action.meta.requestId);
        const index = state.jobs.findIndex((job) => job.id === action.payload.id);
        if (index < 0) state.jobs.unshift(action.payload);
        else if (state.jobs[index].version <= action.payload.version)
          state.jobs[index] = action.payload;
        if (action.type === createReport.fulfilled.type) {
          state.creating = false;
          state.selectedId = action.payload.id;
        }
        state.error = null;
      });
      builder.addCase(thunk.rejected, (state, action) => {
        if (
          state.scope !== action.meta.arg.scope ||
          !state.requests.includes(action.meta.requestId)
        )
          return;
        state.requests = state.requests.filter((id) => id !== action.meta.requestId);
        state.creating = false;
        state.error =
          action.type === pollReport.rejected.type
            ? 'A atualização foi interrompida. Seu relatório continua salvo; tente reconectar.'
            : 'Não foi possível concluir esta ação. Tente novamente.';
      });
    }
    builder.addCase(savePreferences.pending, (state, action) => {
      if (state.scope === action.meta.arg.scope) state.requests.push(action.meta.requestId);
    });
    builder.addCase(savePreferences.fulfilled, (state, action) => {
      if (state.scope !== action.meta.arg.scope || !state.requests.includes(action.meta.requestId))
        return;
      state.requests = state.requests.filter((id) => id !== action.meta.requestId);
      state.preferences = action.payload;
    });
    builder.addCase(savePreferences.rejected, (state, action) => {
      if (state.scope !== action.meta.arg.scope || !state.requests.includes(action.meta.requestId))
        return;
      state.requests = state.requests.filter((id) => id !== action.meta.requestId);
      state.error = 'Não foi possível salvar suas preferências.';
    });
  },
});
export const { resetReports, selectReport } = slice.actions;
export const reportsReducer = slice.reducer;
